/**
 * Hayven's plan catalog (approved 261017): three tiers per location per month, what each tier includes (pools,
 * Google Ranking budget, Growth+ features) and the billing constants the API, the agency app and the Stripe
 * catalog sync share. Prices are cents. One catalog for everyone: a price or pool changes here and nowhere else.
 *
 * Ops features (calendar, booking, inbox, reminders, review requests, Bio page, New Client Offer…) are never gated,
 * so they are not listed: `PlanFeature` names only what a tier can be missing.
 */

export const PLAN_TIERS = ['ESSENTIALS', 'GROWTH', 'PRO'] as const;
export type PlanTier = (typeof PLAN_TIERS)[number];

export const BILLING_INTERVALS = ['MONTH', 'YEAR'] as const;
export type BillingInterval = (typeof BILLING_INTERVALS)[number];

export const SALON_BILLING_STATUSES = ['NO_PLAN', 'COMPED', 'ACTIVE', 'PAST_DUE', 'UNPAID', 'CANCELED'] as const;
export type SalonBillingStatus = (typeof SALON_BILLING_STATUSES)[number];

export const BILLING_COMP_REASONS = ['DEMO', 'TEST', 'PARTNER', 'OTHER'] as const;
export type BillingCompReason = (typeof BILLING_COMP_REASONS)[number];

export const PLAN_FEATURES = [
  'BULK_SMS',
  'REFERRALS',
  'PRIZES',
  'LOYALTY',
  'CREATORS',
  'GOOGLE_RANKING',
  'AI_REVIEW_REPLIES',
  'AI_GBP_POSTS',
  'LANDING_AI',
  'COMMENT_TO_DM',
  'AI_PHONE',
  'PRIORITY_SUPPORT',
  'AGENCY_CAMPAIGN_SETUP',
] as const;
export type PlanFeature = (typeof PLAN_FEATURES)[number];

/** Monthly allowances; the meter and spending cap that enforce them arrive with the pools engine. */
export interface PlanPools {
  /** Marketing text segments (campaigns, marketing automations); MMS = 3. */
  marketingTexts: number;
  /** AI phone assistant minutes. */
  aiPhoneMinutes: number;
  /** Operational text segments before fair use bills them like marketing texts (never blocked). */
  operationalFairUse: number;
}

export interface PlanDefinition {
  tier: PlanTier;
  name: string;
  monthlyPriceCents: number;
  /** 10× monthly: two months free. */
  annualPriceCents: number;
  marketingTextsPerMonth: number;
  aiPhoneMinutesPerMonth: number;
  /** Tracked Google Ranking keywords (0 = no Google Ranking). */
  rankKeywordLimit: number;
  /** Rank scans per salon-local month. */
  rankScansPerMonth: number;
  features: readonly PlanFeature[];
  /** The tier the price table points people to. */
  highlighted: boolean;
  /** Stripe Price `lookup_key` per interval (prices are found by key, never by id or env var). */
  lookupKeys: Record<BillingInterval, string>;
}

const GROWTH_FEATURES = [
  'BULK_SMS',
  'REFERRALS',
  'PRIZES',
  'LOYALTY',
  'CREATORS',
  'GOOGLE_RANKING',
  'AI_REVIEW_REPLIES',
  'AI_GBP_POSTS',
  'LANDING_AI',
  'COMMENT_TO_DM',
  'AI_PHONE',
] as const satisfies readonly PlanFeature[];

export const PLAN_CATALOG: Record<PlanTier, PlanDefinition> = {
  ESSENTIALS: {
    tier: 'ESSENTIALS',
    name: 'Essentials',
    monthlyPriceCents: 19_900,
    annualPriceCents: 199_000,
    marketingTextsPerMonth: 500,
    aiPhoneMinutesPerMonth: 0,
    rankKeywordLimit: 0,
    rankScansPerMonth: 0,
    features: [],
    highlighted: false,
    lookupKeys: { MONTH: 'hayven_essentials_monthly', YEAR: 'hayven_essentials_annual' },
  },
  GROWTH: {
    tier: 'GROWTH',
    name: 'Growth',
    monthlyPriceCents: 29_900,
    annualPriceCents: 299_000,
    marketingTextsPerMonth: 2_000,
    aiPhoneMinutesPerMonth: 200,
    rankKeywordLimit: 4,
    rankScansPerMonth: 30,
    features: GROWTH_FEATURES,
    highlighted: true,
    lookupKeys: { MONTH: 'hayven_growth_monthly', YEAR: 'hayven_growth_annual' },
  },
  PRO: {
    tier: 'PRO',
    name: 'Pro',
    monthlyPriceCents: 49_900,
    annualPriceCents: 499_000,
    marketingTextsPerMonth: 6_000,
    aiPhoneMinutesPerMonth: 500,
    rankKeywordLimit: 10,
    rankScansPerMonth: 60,
    features: [...GROWTH_FEATURES, 'PRIORITY_SUPPORT', 'AGENCY_CAMPAIGN_SETUP'],
    highlighted: false,
    lookupKeys: { MONTH: 'hayven_pro_monthly', YEAR: 'hayven_pro_annual' },
  },
};

/** One-time setup fee, waived on annual plans. */
export const SETUP_FEE_CENTS = 19_900;
export const SETUP_FEE_LOOKUP_KEY = 'hayven_setup_fee';

/** AI phone assistant add-on: only Essentials needs it (Growth and Pro include minutes). */
export const AI_PHONE_ADDON = {
  monthlyPriceCents: 7_900,
  annualPriceCents: 79_000,
  minutes: 150,
  tiers: ['ESSENTIALS'] as readonly PlanTier[],
  lookupKeys: { MONTH: 'hayven_ai_phone_addon_monthly', YEAR: 'hayven_ai_phone_addon_annual' } as Record<
    BillingInterval,
    string
  >,
} as const;

/** Operational texts (reminders, confirmations, text-back, inbox) are unlimited up to this fair-use line. */
export const OPERATIONAL_FAIR_USE_SEGMENTS = 5_000;

/** Usage beyond the pools, billed at month close (never above the salon's spending cap). */
export const OVERAGE = {
  aiPhoneMinuteCents: 30,
  textSegmentCents: 3,
  lookupKeys: { aiPhoneMinute: 'hayven_overage_ai_phone_minute', textSegment: 'hayven_overage_text_segment' },
} as const;

/** Spending cap a new subscription starts with; the salon admin can raise it. */
export const DEFAULT_OVERAGE_CAP_CENTS = 5_000;

/** Highest spending cap a salon admin can set ($2,000/month). */
export const MAX_OVERAGE_CAP_CENTS = 200_000;

/** Days a past-due salon keeps AI phone + marketing sends before they lock (calendar/booking never lock). */
export const PAST_DUE_GRACE_DAYS = 3;

export const PLAN_TIER_LABEL: Record<PlanTier, string> = {
  ESSENTIALS: 'Essentials',
  GROWTH: 'Growth',
  PRO: 'Pro',
};

export const BILLING_INTERVAL_LABEL: Record<BillingInterval, string> = { MONTH: 'Monthly', YEAR: 'Annual' };

export const SALON_BILLING_STATUS_LABEL: Record<SalonBillingStatus, string> = {
  NO_PLAN: 'No card yet',
  COMPED: 'Comped',
  ACTIVE: 'Active',
  PAST_DUE: 'Past due',
  UNPAID: 'Unpaid',
  CANCELED: 'Canceled',
};

export const BILLING_COMP_REASON_LABEL: Record<BillingCompReason, string> = {
  DEMO: 'Demo',
  TEST: 'Test',
  PARTNER: 'Partner',
  OTHER: 'Other',
};

/** Whether the AI phone add-on can be bought on this tier. */
export function aiPhoneAddOnAllowed(tier: PlanTier): boolean {
  return AI_PHONE_ADDON.tiers.includes(tier);
}

/** Whether `tier` (with or without the AI phone add-on) includes `feature`. Pure: billing status is not considered. */
export function planHasFeature(tier: PlanTier, feature: PlanFeature, options: { aiPhoneAddOn?: boolean } = {}): boolean {
  if (PLAN_CATALOG[tier].features.includes(feature)) return true;
  return feature === 'AI_PHONE' && options.aiPhoneAddOn === true && aiPhoneAddOnAllowed(tier);
}

/** Tiers whose plan includes `feature`, cheapest first (catalog order); the add-on is not counted. */
export function tiersWithFeature(feature: PlanFeature): PlanTier[] {
  return PLAN_TIERS.filter((tier) => planHasFeature(tier, feature));
}

/** The cheapest tier that includes `feature`: the one an upgrade prompt names. */
export function lowestTierWithFeature(feature: PlanFeature): PlanTier {
  return tiersWithFeature(feature)[0] ?? 'PRO';
}

/** Every feature as a flag for `tier`. */
export function planFeatureFlags(tier: PlanTier, options: { aiPhoneAddOn?: boolean } = {}): Record<PlanFeature, boolean> {
  return Object.fromEntries(PLAN_FEATURES.map((feature) => [feature, planHasFeature(tier, feature, options)])) as Record<
    PlanFeature,
    boolean
  >;
}

/** Monthly allowances for `tier`; the add-on adds its minutes only where it is allowed. */
export function includedPools(tier: PlanTier, aiPhoneAddOn = false): PlanPools {
  const plan = PLAN_CATALOG[tier];
  const addOnMinutes = aiPhoneAddOn && aiPhoneAddOnAllowed(tier) ? AI_PHONE_ADDON.minutes : 0;
  return {
    marketingTexts: plan.marketingTextsPerMonth,
    aiPhoneMinutes: plan.aiPhoneMinutesPerMonth + addOnMinutes,
    operationalFairUse: OPERATIONAL_FAIR_USE_SEGMENTS,
  };
}

/** Recurring price of a plan (+ add-on) for one interval, in cents. */
export function planPriceCents(tier: PlanTier, interval: BillingInterval, aiPhoneAddOn = false): number {
  const plan = PLAN_CATALOG[tier];
  const base = interval === 'YEAR' ? plan.annualPriceCents : plan.monthlyPriceCents;
  if (!aiPhoneAddOn || !aiPhoneAddOnAllowed(tier)) return base;
  return base + (interval === 'YEAR' ? AI_PHONE_ADDON.annualPriceCents : AI_PHONE_ADDON.monthlyPriceCents);
}
